import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { LiveSnapshotKpis } from '@/types/liveSnapshot'

interface Props {
  kpis: LiveSnapshotKpis
  darkMode: boolean
}

interface Cell {
  key: keyof LiveSnapshotKpis
  /** Petit carré de couleur devant le libellé — repère discret, pas d'icône. */
  square: string
  live?: boolean
}

const CELLS: Cell[] = [
  { key: 'calls_today', square: 'bg-indigo-500' },
  { key: 'missed_today', square: 'bg-rose-500' },
  { key: 'calls_in_progress', square: 'bg-emerald-500', live: true },
  { key: 'agents_available', square: 'bg-slate-400', live: true },
]

/**
 * Bandeau KPI : UNE seule barre, 4 cellules séparées par des filets fins.
 * La valeur d'abord, le libellé dessous — le manager lit quatre chiffres
 * d'un coup d'œil, sans pastilles d'icônes qui gonflent la hauteur.
 */
export function LiveKpiCards({ kpis, darkMode }: Props) {
  const { t } = useTranslation()

  return (
    <div
      className={cn(
        'rounded-[10px] border shadow-sm overflow-hidden',
        darkMode ? 'border-slate-700' : 'border-slate-200',
      )}
    >
      <div
        className={cn(
          'grid grid-cols-2 lg:grid-cols-4 gap-px',
          darkMode ? 'bg-slate-700' : 'bg-slate-200',
        )}
      >
        {CELLS.map(({ key, square, live }) => (
          <div
            key={key}
            className={cn('px-5 py-4', darkMode ? 'bg-slate-800' : 'bg-white')}
          >
            <div className="flex items-start justify-between">
              <div
                className={cn(
                  'text-xl font-bold tabular-nums leading-tight',
                  darkMode ? 'text-white' : 'text-slate-900',
                )}
              >
                {kpis[key]}
              </div>
              {live && (
                <span className="relative inline-flex h-2 w-2 mt-1">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
              )}
            </div>
            <div
              className={cn(
                'mt-0.5 flex items-center gap-1.5 text-xs',
                darkMode ? 'text-slate-400' : 'text-slate-500',
              )}
            >
              <span className={cn('h-[7px] w-[7px] rounded-[2px] flex-shrink-0', square)} />
              {t(`manager.live.kpis.${key}`)}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
